import { useAsync } from "@react-hookz/web";
import { type FunctionComponent, useCallback, useEffect } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { filterButtonCftIndustryQueueQuery } from "scmp-app/queries/__generated__/filterButtonCftIndustryQueueQuery.graphql";

import { Container } from "./styles";

type Props = {
  className?: string;
  isActive: boolean;
  name: string;
  onClick?: (industryName: string) => void;
  onLoad?: (industryName: string, topicIds: string[]) => void;
  queue?: string;
};

export const PlusCftIndustryFilterButton: FunctionComponent<Props> = ({
  className,
  isActive,
  name,
  onClick,
  onLoad,
  queue,
}) => {
  const environment = useRelayEnvironment();

  const [{ result, status }, { execute }] = useAsync(async () => {
    if (!notEmpty(queue)) return;

    const data = await fetchQuery<filterButtonCftIndustryQueueQuery>(
      environment,
      graphql`
        query filterButtonCftIndustryQueueQuery($queue: String!) {
          queue(filter: { name: $queue }) {
            items {
              edges {
                node {
                  ... on Topic {
                    entityUuid
                  }
                }
              }
            }
          }
        }
      `,
      {
        queue,
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise();

    return data?.queue?.items?.edges.map(({ node }) => node.entityUuid).filter(notEmpty) ?? [];
  });

  useEffect(() => {
    if (status !== "not-executed") return;
    void execute();
  }, [execute, status]);

  useEffect(() => {
    if (status !== "success" || !onLoad) return;
    onLoad(name, result ?? []);
  }, [name, onLoad, result, status]);

  const handleClick = useCallback(() => {
    onClick?.(name);
  }, [name, onClick]);

  return (
    <Container $isActive={isActive} className={className} onClick={handleClick}>
      {name}
    </Container>
  );
};

PlusCftIndustryFilterButton.displayName = "PlusCftIndustryFilterButton";
